/**
 * Moon Material
 * TSL-based node material using texture array for tile sampling
 */

import '../../../utils/webgpuPolyfill.js'
import * as THREE from 'three'
import { getCachedTextureArray, setCachedTextureArray } from '../textures/textureCache'

// Dummy envMap — forces materialEnvIntensity to read material.envMapIntensity (0) instead of scene.environmentIntensity
const BLACK_CUBE = new THREE.CubeTexture()
BLACK_CUBE.needsUpdate = false

/**
 * Create a 3D texture (texture array) from individual tile textures
 * @param {Array<Texture>} tileTextures - Array of loaded tile textures
 * @param {number} columns - Grid columns
 * @param {number} rows - Grid rows
 * @param {Object} Data3DTexture - Data3DTexture class from three/webgpu
 * @returns {Data3DTexture}
 */
function createTextureArray(tileTextures, columns, rows, Data3DTexture) {
  if (!tileTextures || tileTextures.length === 0 || !Data3DTexture) return null
  
  const firstTile = tileTextures[0]
  if (!firstTile.image) {
    return null
  }
  
  const tileSize = firstTile.image.width
  const depth = tileTextures.length
  
  const memorySizeMB = (tileSize * tileSize * depth * 4) / (1024 * 1024)
  
  const data = new Uint8Array(tileSize * tileSize * depth * 4)
  
  tileTextures.forEach((tileTex, index) => {
    if (!tileTex.image) {
      return
    }
    
    const canvas = document.createElement('canvas')
    canvas.width = tileSize
    canvas.height = tileSize
    let ctx = canvas.getContext('2d', { willReadFrequently: true })
    
    ctx.clearRect(0, 0, tileSize, tileSize)
    
    const imgWidth = tileTex.image.width
    const imgHeight = tileTex.image.height
    
    ctx.drawImage(tileTex.image, 0, 0, imgWidth, imgHeight, 0, 0, tileSize, tileSize)
    
    const imageData = ctx.getImageData(0, 0, tileSize, tileSize)
    
    const offset = index * tileSize * tileSize * 4
    data.set(imageData.data, offset)
    
    // CRITICAL: Proper GPU memory cleanup
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    // Force GPU context flush
    try {
      ctx.getImageData(0, 0, 1, 1)
    } catch (e) { }
    // Release context reference
    ctx = null
    canvas.width = 0
    canvas.height = 0
    canvas.remove()
  })
  
  const texture = new Data3DTexture(data, tileSize, tileSize, depth)
  texture.format = THREE.RGBAFormat
  texture.type = THREE.UnsignedByteType
  texture.colorSpace = THREE.SRGBColorSpace
  texture.minFilter = THREE.LinearFilter
  texture.magFilter = THREE.LinearFilter
  texture.wrapS = THREE.ClampToEdgeWrapping
  texture.wrapT = THREE.ClampToEdgeWrapping
  texture.wrapR = THREE.ClampToEdgeWrapping
  texture.anisotropy = 16
  texture.unpackAlignment = 1
  texture.needsUpdate = true
  
  return texture
}

/**
 * Create Moon material
 * @param {Object} options
 * @param {Array<Texture>} options.tileTextures - Array of tile textures
 * @param {number} options.columns - Grid columns
 * @param {number} options.rows - Grid rows
 * @param {boolean} options.isWebGPU - Whether WebGPU is available
 * @param {string} options.quality - LOD quality level
 * @param {Object} options.materialConfig - Material configuration
 * @returns {Promise<Material>} Moon material
 */
export async function createMoonMaterial({
  tileTextures = [],
  columns,
  rows,
  isWebGPU = false,
  quality = 'LOD_3',
  materialConfig = {},
}) {
  // If not WebGPU, use WebGL fallback
  if (!isWebGPU) {
    const material = new THREE.MeshStandardMaterial()
    
    if (tileTextures && tileTextures.length > 0 && tileTextures[0]) {
      material.map = tileTextures[0]
      material.map.flipY = false
    } else {
      material.color = new THREE.Color(0xaaaaaa)
    }
    
    material.roughness = materialConfig.baseRoughness || 0.95
    material.metalness = materialConfig.baseMetalness || 0.0
    return material
  }

  // Try to load WebGPU modules
  let webgpuModules = null
  
  try {
    if (typeof GPUShaderStage === 'undefined' && typeof navigator !== 'undefined' && navigator.gpu) {
      window.GPUShaderStage = {
        VERTEX: 0x1,
        FRAGMENT: 0x2,
        COMPUTE: 0x4
      }
    }
    
    const [tsl, webgpu] = await Promise.all([
      import('three/tsl'),
      import('three/webgpu')
    ])
    
    webgpuModules = {
      texture3D: tsl.texture3D,
      uv: tsl.uv,
      floor: tsl.floor,
      fract: tsl.fract,
      vec2: tsl.vec2,
      vec3: tsl.vec3,
      tslFloat: tsl.float,
      add: tsl.add,
      mul: tsl.mul,
      tslClamp: tsl.clamp,
      MeshStandardNodeMaterial: webgpu.MeshStandardNodeMaterial,
      Data3DTexture: webgpu.Data3DTexture
    }
  } catch (e) {
    // Silent fallback
  }

  // WebGL fallback
  if (!webgpuModules) {
    const material = new THREE.MeshStandardMaterial()
    
    if (tileTextures && tileTextures.length > 0 && tileTextures[0]) {
      material.map = tileTextures[0]
      material.map.flipY = false
    } else {
      material.color = new THREE.Color(0xaaaaaa)
    }
    
    material.roughness = materialConfig.baseRoughness || 0.95
    material.metalness = materialConfig.baseMetalness || 0.0
    return material
  }

  const {
    texture3D, uv, floor, fract, vec2, vec3, tslFloat, add, mul, tslClamp,
    MeshStandardNodeMaterial, Data3DTexture
  } = webgpuModules

  // Check cache first
  let textureArray = getCachedTextureArray(quality)
  
  if (!textureArray) {
    if (!tileTextures || tileTextures.length === 0) {
      const material = new MeshStandardNodeMaterial()
      material.colorNode = vec3(0.67, 0.67, 0.67) // Gray moon
    material.roughness = materialConfig.baseRoughness || 0.95
    material.metalness = materialConfig.baseMetalness || 0.0
    material.envMap = BLACK_CUBE
    material.envMapIntensity = 0 // exclude from scene HDRI
    return material
  }
    
    textureArray = createTextureArray(tileTextures, columns, rows, Data3DTexture)
    
    if (textureArray) {
      setCachedTextureArray(quality, textureArray)
    }
  } else {
  }

  if (!textureArray) {
    const material = new MeshStandardNodeMaterial()
    material.colorNode = vec3(0.67, 0.67, 0.67)
    material.roughness = materialConfig.baseRoughness || 0.95
    material.metalness = materialConfig.baseMetalness || 0.0
    material.envMap = BLACK_CUBE
    material.envMapIntensity = 0 // exclude from scene HDRI
    return material
  }


  const material = new MeshStandardNodeMaterial()

  // Get texture depth
  const textureDepth = textureArray.image.depth
  
  // Create TSL function for tile sampling
  const uvCoord = uv()
  const flippedUV = vec2(uvCoord.x, tslFloat(1.0).sub(uvCoord.y))
  
  // Calculate tile indices
  const tileX = floor(mul(flippedUV.x, tslFloat(columns)))
  const tileY = floor(mul(flippedUV.y, tslFloat(rows)))
  
  const clampedX = tslClamp(tileX, tslFloat(0), tslFloat(columns - 1))
  const clampedY = tslClamp(tileY, tslFloat(0), tslFloat(rows - 1))
  
  // Local UV within tile
  const localUV = fract(mul(flippedUV, vec2(columns, rows)))
  
  // Calculate flat tile index
  const tileIndex = add(mul(clampedY, tslFloat(columns)), clampedX)
  
  // Normalize for texture array sampling
  const normalizedZ = mul(add(tileIndex, tslFloat(0.5)), tslFloat(1.0 / textureDepth))
  
  // Sample from 3D texture array
  const uvw = vec3(localUV.x, localUV.y, normalizedZ)
  const diffuseSample = texture3D(textureArray, uvw)
  
  material.colorNode = diffuseSample
  material.roughness = materialConfig.baseRoughness || 0.95
  material.metalness = materialConfig.baseMetalness || 0.0
  material.envMap = BLACK_CUBE
  material.envMapIntensity = 0 // exclude from scene HDRI
  
  // Store reference for cleanup
  material.userData.textureArray = textureArray
  
  return material
}

export default createMoonMaterial
